
import { Fragment, useState, useEffect } from 'react';

import useFirstRender from '@klv/hooks/useFirstRender';
import Image from '@klv/atoms/image';
import TranslatedDate from '@klv/atoms/translated-date';
import Buttons from '@klv/molecules/buttons';
import { generateClassName } from '@klv/shared/utils/functions';
import type { IGatsbyImageData } from 'gatsby-plugin-image';

import * as styles from './styles/bfcmCalendar.module.css';

import type { IBfcmCalendar } from './types';

export const BfcmCalendar = ({
  ctaBody,
  ctaHeadline,
  buttons,
  desktopImage,
  mobileImage,
  jumpLinkLabel,
  bfcmDates,
}: IBfcmCalendar) => {

  const firstRender = useFirstRender();
  const months = Array.from(new Set(bfcmDates.map(date => (new Date(date.templatesBfcmCalendar.postDate)).toLocaleString('default', { month: 'long' }))));
  const [selected, setSelected] = useState<string | null>(months[0] || null);
  

  useEffect(() => {
    if (selected && !firstRender) {
      const selectedMonth = document.getElementById(`timeline-${selected}`);
      const scrollTo = selectedMonth?.getBoundingClientRect().top;

      if (scrollTo) {
        window.scrollTo({
          top: document?.documentElement?.scrollTop + scrollTo - 200,
          behavior: 'smooth',
        });
      }
    }
  }, [selected, firstRender]);

  return (
    <>
      <div className={styles.bfcmCalendarLinkBar}>
        <div className={'grid'}>
          <div className={styles.mobileLinks}>
            <div className={styles.mobileSelectWrapper}>
              <select className={styles.mobileSelect} defaultValue={selected || ''} onChange={(e) => setSelected(e.target.value)}>
                {months.map((monthName) => (
                  <option key={monthName} value={monthName}>
                    {monthName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.desktopLinks}>
            <div className={styles.jumpLinkLabel}>{jumpLinkLabel}</div>
            {months.map((monthName) => (
              <a key={monthName} onClick={() => setSelected(monthName)} className={generateClassName([(selected === monthName ? styles.selectedDesktopLink : '')])}>{monthName}</a>
            ))}
          </div>
        </div>
      </div>
      <div className={generateClassName(['grid', styles.bfcmCalendar])}>
        <div className={styles.sideBar}>
          <div className={styles.desktopImage}>
            <Image
              imageData={desktopImage?.gatsbyImage as IGatsbyImageData}
              altText={desktopImage?.altText}
            />
          </div>
          <div className={styles.mobileImage}>
            <Image
              imageData={mobileImage?.gatsbyImage as IGatsbyImageData}
              altText={mobileImage?.altText}
            />
          </div>
          <div className={styles.ctaHeadline}>{ctaHeadline}</div>
          <p className={styles.ctaBody}>{ctaBody}</p>
          <Buttons className={styles.ctaButtons} buttons={buttons} variants={['primary']} buttonColor='dark'/>
        </div>

        <div className={styles.calendar}>
          {bfcmDates.map((date, index) => {
            const lastMonth = index > 0 ? (new Date(bfcmDates[index - 1].templatesBfcmCalendar.postDate)).toLocaleString('default', { month: 'long' }) : null;
            const thisMonth = (new Date(date.templatesBfcmCalendar.postDate)).toLocaleString('default', { month: 'long' });
            return (
            <Fragment key={date.title}>
              {(lastMonth !== thisMonth) ? (
                <div className={styles.month}>
                  <a id={`timeline-${thisMonth}`}/>
                  <span className={styles.monthIcon}></span>
                  <span className={styles.monthText}>{thisMonth}</span>
                </div>
              ) : (
                <div className={styles.postDivider}>
                  <span className={styles.connectorIcon}></span>
                </div>
              )}
              <div className={styles.calendarDate}>
                <div className={styles.cardLabel}>
                  <TranslatedDate date={date.templatesBfcmCalendar.postDate} options={{
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }}/>
                </div>
                <div className={generateClassName([styles.title, 'h4', 'semibold'])} dangerouslySetInnerHTML={{ __html: date?.title || '' }} />
                <div className={generateClassName([styles.description, 'p2'])} dangerouslySetInnerHTML={{ __html: date?.templatesBfcmCalendar?.description || '' }} />
                <Buttons className={styles.cardButtons} buttons={date.templatesBfcmCalendar.buttons} variants={['tertiary']} buttonColor='dark'/>
              </div>
            </Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
};
